/**
 * Copyright (c) 2019, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Localized from '../../../shared/components/Localized';
import { TIMESTAMP_FORMAT } from '../../../shared/utils/dates';
import DataTable from '../../components/DataTable';
import Error from '../../components/Error';
import LoadingSpinner from '../../components/Spinners/LoadingSpinner.js';
import styles from '../../styles';
import Header from './components/Header.js';

const GENERAL_MESSAGE_HISTORY_IN_DAYS = 90;

const Wrapper = styled.div`
  @media (min-width: 768px) {
    ${p => !p.fullscreen && 'padding: 0 0.75rem 0 0.75rem;'}
  }
`;

const Pane = styled.div`
  height: 100%;
  position: relative;
  background-color: white;
  margin-bottom: 1rem;
  ${styles.borders.card}
  backgorund-color: gray;
  ${p => p.fullscreen && 'border-radius: 0;'}
  ${p => p.fullscreen && 'box-shadow: none;'}
  @media (max-width: 767px) {
    border-radius: 0;
  }
`;

const Body = styled.div`
  min-height: 20rem;
`;

export const GeneralMessagesTable = styled(DataTable)`
  table {
    border-radius: 0.25rem;
    border: solid 1px #d8dde6;
  }
  td {
    width: auto;
  }
  @media (min-width: 1023px) {
    padding: 1rem;
  }
`;

const LocalizedOngoingMessage = styled(Localized)`
  color: #04844b;
`;

const LocalizedClosedMessage = styled(Localized)`
  color: #54698d;
`;

const isActive = message => {
  return message.endDate === null || moment(message.endDate).isAfter(moment());
};

// The cutoff date is currently 90 days prior to today
const cutOffDate = moment().subtract(GENERAL_MESSAGE_HISTORY_IN_DAYS, 'd');

/**
 * General Message Table component
 * This component displays a history of general messages.
 * It displays both ongoing as well as past general messages.
 */
class GeneralMessages extends React.Component {
  constructor(props) {
    super(props);
  }
  render() {
    const { requests, fullscreen, region } = this.props;
    const { generalMessages } = requests;
    const { isCompleted, isErrored, value } = generalMessages;

    // If there was an error show the error page
    if (isErrored) return <Error />;

    // Show a loading indicator while the request is being processed
    if (!isCompleted) return <LoadingSpinner inverse />;

    // Sort the result by id, and make sure that we display messages from last 33 days
    const rows = value
      .sort((a, b) => (a.id < b.id ? 1 : -1))
      .filter(message => {
        return message.createdAt.isAfter(cutOffDate);
      });

    const messageCount = rows.length || 0;
    const columns = [
      {
        title: (
          <Localized
            modelKey="id"
            modelAttribute="header"
            modelName="generalMessage"
          />
        ),
        name: 'id',
        render: row => (
          <Link
            key={row.id}
            to={{
              pathname: `/generalmessages/${row.id}`,
              state: { modal: true }
            }}
          >
            {row.id}
          </Link>
        ),
        key: true,
        sizeLarge: 2
      },
      {
        title: (
          <Localized
            modelKey="subject"
            modelAttribute="header"
            modelName="generalMessage"
          />
        ),
        name: 'subject',
        render: row => row.subject,
        sizeLarge: 6
      },
      {
        title: (
          <Localized
            modelKey="posted"
            modelAttribute="header"
            modelName="generalMessage"
          />
        ),
        name: 'posted',
        hideSmall: true,
        render: row =>
          moment.tz(row.createdAt, region).format(TIMESTAMP_FORMAT),
        sizeLarge: 2
      },
      {
        title: (
          <Localized
            modelKey="active"
            modelAttribute="header"
            modelName="generalMessage"
          />
        ),
        name: 'active',
        render: row =>
          isActive(row) ? (
            <LocalizedOngoingMessage
              modelKey="Ongoing"
              modelAttribute="label"
              modelName="generalMessage"
            />
          ) : (
            <LocalizedClosedMessage
              modelKey="Closed"
              modelAttribute="header"
              modelName="generalMessage"
            />
          ),
        sizeLarge: 2
      }
    ];

    return (
      <Wrapper fullscreen={fullscreen}>
        <Pane fullscreen={fullscreen}>
          <Header messageCount={messageCount} />
          <Body>
            <GeneralMessagesTable
              showColumns
              noneText={'No General Message'}
              rows={rows}
              columns={columns}
            />
          </Body>
        </Pane>
      </Wrapper>
    );
  }
}

GeneralMessages.propTypes = {
  requests: PropTypes.object,
  fullscreen: PropTypes.object,
  region: PropTypes.string
};

export default GeneralMessages;
